import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Integrations.css';
import {
	Dialog,
	EmptyState,
	IntegrationMetadata,
	MerchantIntegration,
	Spinner,
	useDeferredLoading,
} from '@anticatrade/shared';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { AppContext, appContext } from '../../../context/AppContext';
import { Heading } from '../../base/Heading/Heading';
import { useIntegrations } from './useIntegrations';

const Integrations = () => {
	const [toRemove, setToRemove] = useState<MerchantIntegration | null>(null);
	const [isRemoving, setIsRemoving] = useState<boolean>(false);

	const { t } = useTranslation();
	const { showStatus, handleError, redirect } = useContext(
		appContext as React.Context<AppContext>,
	);
	const { isLoading, available, installed, removeIntegration } = useIntegrations();
	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const installedByKey = useMemo(() => {
		const map = new Map<string, MerchantIntegration>();
		for (const i of installed) {
			map.set(i.integrationKey, i);
		}
		return map;
	}, [installed]);

	const onConfirmRemove = async () => {
		if (toRemove == null) {
			return;
		}
		setIsRemoving(true);
		try {
			await removeIntegration(toRemove.id);
		} catch (err) {
			handleError(err as Error);
			setIsRemoving(false);
			return;
		}
		setIsRemoving(false);
		setToRemove(null);
		showStatus({
			variant: 'success',
			icon: 'check-circle',
			text: t('Integration removed successfully'),
		});
	};

	return (
		<div className='integrations'>
			{showContent ? (
				<div className='integrations__content'>
					<Heading left={<h1 className='integrations__title'>{t('Integrations')}</h1>} />
					{available.length === 0 ? (
						<EmptyState
							icon='plug'
							title={t('No integrations available yet')}
							description={t(
								'New channels to publish your items on will appear here',
							)}
						/>
					) : (
						<div className='integrations__list'>
							{available.map((meta) => {
								const mi = installedByKey.get(meta.key) ?? null;
								return (
									<IntegrationCard
										key={meta.key}
										metadata={meta}
										integration={mi}
										onAdd={() => redirect(`integrations/connect/${meta.key}`)}
										onDetails={() =>
											mi != null && redirect(`integrations/details/${mi.id}`)
										}
										onRemove={() => setToRemove(mi)}
									/>
								);
							})}
						</div>
					)}
				</div>
			) : showSpinner ? (
				<Spinner />
			) : null}

			<Dialog
				variant='danger'
				title={t('Remove integration')}
				isOpen={toRemove != null}
				onAfterHide={() => setToRemove(null)}
				actions={
					<>
						<SlButton onClick={() => setToRemove(null)}>{t('Cancel')}</SlButton>
						<SlButton variant='danger' loading={isRemoving} onClick={onConfirmRemove}>
							{t('Remove')}
						</SlButton>
					</>
				}
			>
				{t(
					'Removing this integration will not delete what you already published. Continue?',
				)}
			</Dialog>
		</div>
	);
};

interface IntegrationCardProps {
	metadata: IntegrationMetadata;
	integration: MerchantIntegration | null;
	onAdd: () => void;
	onDetails: () => void;
	onRemove: () => void;
}

const IntegrationCard = ({
	metadata,
	integration,
	onAdd,
	onDetails,
	onRemove,
}: IntegrationCardProps) => {
	const { t } = useTranslation();
	const isInstalled = integration != null;

	return (
		<div className='integration-card'>
			<div className='integration-card__header'>
				<img
					className='integration-card__logo'
					src={metadata.logoURL}
					alt={metadata.title}
				/>
				{isInstalled && (
					<span className='integration-card__badge'>
						<SlIcon name='check-circle-fill' />
						{t('Installed')}
					</span>
				)}
			</div>
			<div className='integration-card__title'>{metadata.title}</div>
			<div className='integration-card__description'>{metadata.description}</div>
			<div className='integration-card__actions'>
				{isInstalled ? (
					<>
						<SlButton onClick={onDetails}>{t('Details')}</SlButton>
						<SlButton variant='danger' outline onClick={onRemove}>
							{t('Remove')}
						</SlButton>
					</>
				) : (
					<SlButton variant='default' onClick={onAdd}>
						{t('Connect')}
					</SlButton>
				)}
			</div>
		</div>
	);
};

export { Integrations };
